/**
 * The pure half of the ops route composition, mirroring
 * `src/app/routes/compose.ts` one level down: `./index.ts` holds the
 * `import.meta.glob` call, resolved by Vite at build time from the literal
 * it is written with, and everything it does with the result lives here,
 * where it can be exercised against a hand-built module map instead of
 * waiting on the ops areas themselves to exist on disk.
 */

import type { OpsNavEntry, OpsRouteBundle } from './contract';

function isOpsRouteBundle(value: unknown): value is OpsRouteBundle {
  if (value === null || typeof value !== 'object') return false;
  const candidate = value as { routes?: unknown; nav?: unknown; order?: unknown };
  return (
    Array.isArray(candidate.routes) &&
    Array.isArray(candidate.nav) &&
    typeof candidate.order === 'number'
  );
}

/**
 * Collects the `bundle` export of every discovered ops area.
 *
 * Throws, rather than skipping, when a discovered module does not export a
 * usable `bundle` -- a file at `src/ops/<area>/routes.ts` exists because
 * somebody created it to register an area, and silently ignoring a typo in
 * its export name would present as "my area never appeared" with nothing to
 * read.
 */
export function bundlesFrom(modules: Record<string, unknown>): OpsRouteBundle[] {
  return Object.keys(modules)
    .sort()
    .map((path) => {
      const candidate = (modules[path] as { bundle?: unknown } | undefined)?.bundle;
      if (!isOpsRouteBundle(candidate)) {
        throw new Error(
          `${path} was discovered as an ops area but exports no \`bundle: OpsRouteBundle\``,
        );
      }
      return candidate;
    });
}

/**
 * Every bundle's nav entries, sorted by the bundle's declared `order` and
 * then by the entry's own path -- never by the filesystem order the glob
 * happened to return, which is a property of directory names rather than of
 * the product.
 */
export function navFrom(bundles: OpsRouteBundle[]): OpsNavEntry[] {
  return bundles
    .flatMap((bundle) => bundle.nav.map((entry) => ({ entry, order: bundle.order })))
    .sort((a, b) => a.order - b.order || a.entry.path.localeCompare(b.entry.path))
    .map(({ entry }) => entry);
}
